import { AxiosPromise } from 'axios'

import api from '@config/api'
import apiExterno from '@config/api-externo'

// ─── Etapa 1 — Config ────────────────────────────────────────────────────────

export function getConfigs(): AxiosPromise {
  return api.get('v1/FollowUpFornecedorConfig')
}

export function createConfig(data: {
  emails: string[]
  supplierIds: number[]
  storeIds: number[]
}): AxiosPromise {
  return api.post('v1/FollowUpFornecedorConfig', data)
}

export function updateConfig(
  grupoId: string,
  data: {
    emails: string[]
    supplierIds: number[]
    storeIds: number[]
  }
): AxiosPromise {
  return api.put(`v1/FollowUpFornecedorConfig/${grupoId}`, data)
}

export function deleteConfig(grupoId: string): AxiosPromise {
  return api.delete(`v1/FollowUpFornecedorConfig/${grupoId}`)
}

export function getHistoricoConfig(params: {
  take: number
  skip: number
}): AxiosPromise {
  return api.get('v1/FollowUpFornecedorConfig/Historico', { params })
}

// ─── Etapa 2 — Externo (sem auth) ────────────────────────────────────────────

export function getExternoByToken(token: string, codigo: string): AxiosPromise {
  return apiExterno.get(`v1/FollowUpFornecedorExterno/${token}`, {
    headers: { 'X-Followup-Codigo': codigo }
  })
}

export function submitExterno(
  token: string,
  codigo: string,
  itens: Array<{
    documentId: number
    productId: number
    novaQuantidade: number | null
    novaDataEntrega: string | null
    observacao: string | null
  }>
): AxiosPromise {
  return apiExterno.post(`v1/FollowUpFornecedorExterno/${token}`, {
    codigo,
    itens
  })
}

// ─── Etapa 3 — Aprovação ─────────────────────────────────────────────────────

type AprovacaoFiltroBody = {
  fornecedores: { ids: number[] }
  centros: { ids: number[] }
  gerenteProduto?: { ids: number[] }
  categoriaCompacta?: { ids: number[] }
  statusAprovacao: number[]
  dataInicio?: string
  dataFim?: string
  usuarios?: { ids: number[] }
}

export function getAprovacao(body: AprovacaoFiltroBody): AxiosPromise {
  return api.post('v1/FollowUpFornecedor/Aprovacao/filtrar', body)
}

export function getUsuariosResponsaveisAprovacao(): AxiosPromise {
  return api.get('v1/FollowUpFornecedor/Aprovacao/usuarios-responsaveis')
}

export function exportarAprovacao(body: AprovacaoFiltroBody): AxiosPromise {
  return api.post('v1/FollowUpFornecedor/Aprovacao/exportar', body, {
    responseType: 'blob'
  })
}

export function submitAprovacao(
  itens: Array<{
    itemId: number
    statusAprovacao: number
    observacaoAprovador: string | null
  }>
): AxiosPromise {
  return api.post('v1/FollowUpFornecedor/Aprovacao', { itens })
}

// ─── Etapa 3.5 — Revisão Comprador (lote) ────────────────────────────────────

export function importarCSVRevisao(file: File): AxiosPromise {
  const form = new FormData()
  form.append('file', file)
  return api.post('v1/FollowUpFornecedor/revisao-comprador/arquivo', form)
}

// ─── Etapa 4 — Listagem ───────────────────────────────────────────────────────

export function getListagem(body: {
  fornecedores: { ids: number[] }
  centros: { ids: number[] }
  statusAprovacao: number[]
  statusDisparo: number[]
  dataInicio?: string
  dataFim?: string
  take: number
  skip: number
  usuarios?: { ids: number[] }
  pedidos?: { descricoes: string[] }
  produtos?: { ids: number[] }
}): AxiosPromise {
  return api.post('v1/FollowUpFornecedor/filtrar', body)
}

export function getCards(body: {
  fornecedores: { ids: number[] }
  centros: { ids: number[] }
  statusAprovacao: number[]
  dataInicio?: string
  dataFim?: string
  usuarios?: { ids: number[] }
  pedidos?: { descricoes: string[] }
  produtos?: { ids: number[] }
}): AxiosPromise {
  return api.post('v1/FollowUpFornecedor/cards', body)
}

export function exportarListagem(body: {
  fornecedores: { ids: number[] }
  centros: { ids: number[] }
  statusAprovacao: number[]
  statusDisparo: number[]
  dataInicio?: string
  dataFim?: string
  usuarios?: { ids: number[] }
  pedidos?: { descricoes: string[] }
  produtos?: { ids: number[] }
}): AxiosPromise {
  return api.post('v1/FollowUpFornecedor/exportar', body, {
    responseType: 'blob'
  })
}

export function getHistoricoPar(params: {
  documentId: number
  productId: number
}): AxiosPromise {
  return api.get('v1/FollowUpFornecedor/historico-par', { params })
}

// ─── Responsáveis por Aprovação ───────────────────────────────────────────────

export function getResponsaveis(): AxiosPromise {
  return api.get('v1/FollowUpAprovacaoResponsavel')
}

export function createResponsaveis(data: {
  itens: Array<{
    supplierId: number
    compactCategoryId: number | null
    userEmail: string
  }>
}): AxiosPromise {
  return api.post('v1/FollowUpAprovacaoResponsavel', data)
}

export function deleteResponsaveis(supplierId: number): AxiosPromise {
  return api.delete(`v1/FollowUpAprovacaoResponsavel/${supplierId}`)
}

export function getHistoricoResponsaveis(params: {
  take: number
  skip: number
}): AxiosPromise {
  return api.get('v1/FollowUpAprovacaoResponsavel/historico', { params })
}

export function getUsuariosResponsaveis(): AxiosPromise {
  return api.get('v1/FollowUpAprovacaoResponsavel/usuarios')
}

export enum StatusAprovacao {
  Aguardando = 0,
  Aprovado = 1,
  Recusado = 2,
  Invalido = 3
}

export enum StatusDisparo {
  AguardandoEnvio = 0,
  EmailEnviado = 1,
  Submetido = 2,
  Invalido = 3,
  Expirado = 4,
  ErroEnvio = 5
}

export default {
  importarCSVRevisao,
  getConfigs,
  createConfig,
  updateConfig,
  deleteConfig,
  getHistoricoConfig,
  getExternoByToken,
  submitExterno,
  getAprovacao,
  getUsuariosResponsaveisAprovacao,
  exportarAprovacao,
  submitAprovacao,
  getListagem,
  getCards,
  exportarListagem,
  getHistoricoPar,
  getResponsaveis,
  createResponsaveis,
  deleteResponsaveis,
  getHistoricoResponsaveis,
  getUsuariosResponsaveis
}
